/**
 * 日粒度日期筛选器组件
 */
import React, { useEffect, useState, useMemo } from 'react';
import moment, { Moment } from 'moment';
import { EntryProps } from '../interface';
import { moment2String } from '@/pages/datalab/components/SortableDrag/utils';
import RangePicker from '@/pages/home/components/RangePicker';
import RangePickerLeft from '@/pages/dashboard/components/RangePickerLeft';
import { getExtraShortcut } from '@/pages/dashboard/components/NewFilter/FilterBase/getExtraShortcut';
import { START_OF_DATE } from '@/pages/dashboard/utils/_config/constant';
import { getLocaleState } from '@home/utils/state';
import { DATALAB_DATE_EVENT } from '@dbl/core/global';
import type { TDatalabDateEvent } from '@dbl/core/global';

const START_OF_WEEK = 1;

// 获取默认日期范围：过去7天（从7天前到昨天）
const getDefaultDateRange = (): [Moment, Moment] => {
  const yesterday = moment().subtract(1, 'day');
  const sevenDaysAgo = moment().subtract(7, 'day');
  return [sevenDaysAgo, yesterday];
};

// 获取截止日期（昨天）
const getLatestDate = () => {
  const yesterday = moment().subtract(1, 'day');
  return {
    date: yesterday,
  };
};

export const FilterDateEntry = (props: EntryProps) => {
  const { column, onChange, isEn } = props;
  
  // 默认值：过去7天
  const defaultDateRange = useMemo(() => getDefaultDateRange(), []);
  const latestDate = useMemo(() => getLatestDate(), []);
  const startOfTime = START_OF_DATE;
  const { data: seasonInfo } = getLocaleState<TDatalabDateEvent>(DATALAB_DATE_EVENT) || {};
  
  // RangePickerLeft 需要 latestDate 对象，但接口定义不准确，使用类型断言
  const latestDateForShortcut = latestDate as any;

  // 将 column.column_value 转换为 Moment 数组
  const getDateValueFromColumn = (): [Moment | null, Moment | null] | null => {
    const columnValues = column.column_value ?? [];
    if (columnValues.length === 0) {
      return defaultDateRange;
    }
    if (columnValues.length >= 2) {
      const start = moment(columnValues[0]);
      const end = moment(columnValues[1]);
      if (start?.isValid() && end?.isValid()) {
        return [start, end];
      }
    }
    return defaultDateRange;
  };

  const [dateValue, setDateValue] = useState<[Moment | null, Moment | null] | null>(
    getDateValueFromColumn()
  );

  // 同步外部传入的 value 到内部状态
  useEffect(() => {
    const newValue = getDateValueFromColumn();
    setDateValue(newValue);
  }, [JSON.stringify(column.column_value)]);

  // 处理日期变化
  const handleChange = (newValue: [Moment | null, Moment | null] | null): void => {
    setDateValue(newValue);
    
    // 转换为字符串数组格式，符合 EntryProps 的 onChange 接口
    if (newValue?.[0] && newValue?.[1]) {
      const startStr = moment2String(newValue[0], undefined);
      const endStr = moment2String(newValue[1], undefined);
      onChange('between', [startStr, endStr]);
    } else {
      onChange('between', []);
    }
  };

  return (
    <>
      <span className="dbl-fs-entry-text-prefix">
        {isEn ? 'on' : '在'}
      </span>
      <div className="dbl-fs-entry-date-picker-wrapper search-item-date">
        <RangePicker
          type="date"
          latestDate={latestDate}
          value={dateValue}
          startOfTime={startOfTime}
          setShortcutOption={() => (
            <RangePickerLeft
              latestDate={latestDateForShortcut}
              startOfTime={startOfTime}
              seasonInfo={seasonInfo}
              activeTab="common"
              handleChangeValue={() => {}}
              setSelectLeftType={() => {}}
              selectedLeftType="common"
              childRef={React.createRef()}
              type="date"
              dateFilterKey="normal"
            />
          )}
          className="dbl-fs-entry-date-picker"
          overlayClassName="dbl-fs-entry-date-picker-overlay dbd-range-picker-overlay"
          typeList={['date']}
          showSelectType={false}
          startOfWeek={START_OF_WEEK}
          language={isEn ? 'en' : 'cn'}
          onChange={handleChange}
          defaultValue={{
            date: defaultDateRange,
          }}
          extraShortcut={getExtraShortcut(seasonInfo)}
        />
      </div>
    </>
  );
};
